import { createContext, useContext, type ReactNode } from 'react'
import type { LivePlatform } from '../store/idea-store'

/**
 * One Post model: a platform tab renders the SAME canvas surface as the Post
 * tab, and the sections themselves carry the contract —
 *   media    → shared with the post ("Edit on the post" routes to the Post tab)
 *   text     → auto-adapted copy; a gate ("Customize for {platform}?") unlocks
 *              inline editing, which pins the variant on first commit
 * `customized` flips text sections to normal editing. Absent context (the Post
 * tab, PostModal, standalone creatives) leaves every surface untouched.
 */
export interface VariantMode {
  platform: LivePlatform
  platformLabel: string
  /** Pinned in the DB, or unlocked through the gate this session. */
  customized: boolean
  /** Open the "Customize for {platform}?" gate dialog. */
  onCustomizeRequest: () => void
  /** Route to the Post view (shared visuals are edited there). */
  onGoToPost: () => void
}

const VariantModeContext = createContext<VariantMode | null>(null)

export function VariantModeProvider({
  value,
  children,
}: {
  value: VariantMode | null
  children: ReactNode
}) {
  return <VariantModeContext.Provider value={value}>{children}</VariantModeContext.Provider>
}

export function useVariantMode(): VariantMode | null {
  return useContext(VariantModeContext)
}
